<template>
<div class="da-wrapper">
    <!-- Mientras carga: el loader va pegado al header y ocupa justo el resto de
         la pantalla (sin el hueco del padding del layout ni scroll).
         Al terminar se desvanece encima del contenido, que entra suave. -->
    <transition name="da-loader-out">
        <div v-if="!accommodation && !noEncontrado" class="da-loading" :style="{ '--da-header': headerH + 'px' }">
            <Loader :relative="true" />
        </div>
    </transition>
    <!-- No existe, se eliminó o está sin aprobar (y no es tuyo): la 404 del
         sitio. Antes se quedaba cargando para siempre. -->
    <NotFound v-if="noEncontrado && !accommodation" />
    <transition name="da-content-in">
    <div v-if="accommodation" class="fx" :style="{ '--fx-header': headerH + 'px' }">
      <div class="fx-wrap">

        <!-- ══ CABECERA ══════════════════════════════════════════════════ -->
        <div class="fx-crumb">
            <button type="button" class="fx-pill" @click="compartir">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M7.5 7.5 12 3l4.5 4.5" /><path d="M5 12v7.5A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V12" /></svg>
                <span>{{ $t('ficha_nueva.share') }}</span>
            </button>
        </div>

        <header class="fx-head">
            <h1 class="fx-title" tabindex="-1">{{ displayTitle | capitalFirst }}</h1>
            <div class="fx-meta">
                <template v-if="isBuilding">
                    <span class="fx-idp">{{ $tc('ficha_nueva.units', accommodation.propiedad.total_unidades || buildingUnits.length, { n: accommodation.propiedad.total_unidades || buildingUnits.length }) }}</span>
                </template>
                <template v-else>
                    <button type="button" class="fx-idp" :aria-label="$t('ficha_nueva.copy_id', { id: accommodation.idalojamiento })" @click="copiar(accommodation.idalojamiento)">
                        ID <b>{{ accommodation.idalojamiento }}</b>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8.5" y="8.5" width="12" height="12" rx="2.5" /><path d="M15.5 8.5V6A2.5 2.5 0 0 0 13 3.5H6A2.5 2.5 0 0 0 3.5 6v7A2.5 2.5 0 0 0 6 15.5h2.5" /></svg>
                    </button>
                    <button v-if="accommodation.idcodigo" type="button" class="fx-idp" :aria-label="$t('ficha_nueva.copy_code', { code: accommodation.idcodigo })" @click="copiar(accommodation.idcodigo)">
                        {{ $t('ficha_nueva.code') }} <b>{{ accommodation.idcodigo }}</b>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8.5" y="8.5" width="12" height="12" rx="2.5" /><path d="M15.5 8.5V6A2.5 2.5 0 0 0 13 3.5H6A2.5 2.5 0 0 0 3.5 6v7A2.5 2.5 0 0 0 6 15.5h2.5" /></svg>
                    </button>
                    <span v-if="accommodation.dni && accommodation.dni !== 'null'" class="fx-idp">
                        {{ $t('ficha_nueva.registry') }} (<a href="https://buenosaires.gob.ar/tramites/inscripcion-en-el-registro-de-alquileres-temporarios" target="_blank" rel="noopener">ATT</a>): <b>{{ accommodation.dni }}</b>
                    </span>
                </template>
            </div>
        </header>

        <!-- ══ FOTOS | MAPA (en el mismo lugar) ══════════════════════════ -->
        <section :class="['fx-md', { 'is-map': vista === 'mapa' }]" :aria-label="$t('ficha_nueva.media')">
            <div class="fx-md-tabs" role="tablist" :aria-label="$t('ficha_nueva.media')">
                <button type="button" role="tab" class="fx-md-tab" :aria-selected="String(vista === 'fotos')" @click="vista = 'fotos'">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" /></svg>
                    {{ $t('ficha_nueva.photos') }}
                </button>
                <button type="button" role="tab" class="fx-md-tab" :aria-selected="String(vista === 'mapa')" @click="vista = 'mapa'">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 4-6 2.5v13.5l6-2.5 6 2.5 6-2.5V4l-6 2.5L9 4Z" /><path d="M9 4v13.5M15 6.5V20" /></svg>
                    {{ $t('ficha_nueva.map') }}
                </button>
            </div>

            <template v-if="vista === 'fotos'">
                <div v-if="fotos.length" :class="['fx-gal', 'fx-gal--' + mosaico.length]">
                    <button v-for="(f, i) in mosaico" :key="'m' + i" type="button" class="fx-gal-i" :aria-label="$t('ficha_nueva.gal.photo_n', { n: i + 1, total: fotos.length })" @click="abrirGaleria(i)">
                        <img :src="f" alt="" :loading="i > 2 ? 'lazy' : null" />
                    </button>
                    <button type="button" class="fx-pill fx-gal-all" @click="abrirGaleria(0)">{{ $tc('ficha_nueva.see_photos', fotos.length, { n: fotos.length }) }}</button>
                </div>
                <div v-if="fotos.length" class="fx-gal-m">
                    <div ref="pista" class="fx-gal-m-track" @scroll.passive="alDeslizar">
                        <img v-for="(f, i) in fotos" :key="'c' + i" :src="f" :alt="$t('ficha_nueva.gal.photo_n', { n: i + 1, total: fotos.length })" :loading="i ? 'lazy' : null" @click="abrirGaleria(i)" />
                    </div>
                    <span class="fx-gal-m-count">{{ fotoMovil + 1 }} / {{ fotos.length }}</span>
                </div>
                <div v-if="!fotos.length" class="fx-gal-empty">{{ $t('ficha_nueva.no_photos') }}</div>
            </template>
            <div v-else class="fx-md-map">
                <DetalleAlojamientoLocation :data="accommodation" />
            </div>
        </section>

        <!-- ══ CUERPO ════════════════════════════════════════════════════ -->
        <div class="fx-lay">
            <div class="fx-main">

                <!-- Edificio: lista de unidades (misma lógica que antes) -->
                <section v-if="isBuilding" class="fx-card" aria-labelledby="fx-units-t">
                    <h2 id="fx-units-t" class="fx-sec-t">{{ $t('ficha_nueva.units_title') }}</h2>
                    <p class="fx-sec-d">{{ $tc('ficha_nueva.units_desc', buildingUnits.length, { n: buildingUnits.length }) }}</p>
                    <div class="fx-units">
                        <button
                            v-for="unit in buildingUnits"
                            :key="unit.idalojamiento"
                            type="button"
                            class="fx-unit"
                            :aria-pressed="String(!!(selectedUnit && selectedUnit.idalojamiento === unit.idalojamiento))"
                            @click="selectUnit(unit)"
                        >
                            <span class="fx-unit-ph">
                                <img v-if="unit.Photos && unit.Photos.length" :src="photoUrl(unit.Photos[0].url)" alt="" loading="lazy" />
                                <span v-if="selectedUnit && selectedUnit.idalojamiento === unit.idalojamiento" class="fx-tag">✓ {{ $t('ficha_nueva.selected') }}</span>
                            </span>
                            <span class="fx-unit-b">
                                <span class="fx-unit-top">#{{ unit.idalojamiento }}<small v-if="unit.idcodigo">{{ unit.idcodigo }}</small></span>
                                <span class="fx-unit-t">
                                    <template v-if="unit.m2">{{ unit.m2 }} m²</template>
                                    <template v-if="unit.m2 && unit.maximo_huespedes"> · </template>
                                    <template v-if="unit.maximo_huespedes">{{ $tc('ficha_nueva.guests', unit.maximo_huespedes, { n: unit.maximo_huespedes }) }}</template>
                                </span>
                                <span v-if="unit.arriendo_total || unit.arriendo_diario" class="fx-unit-price">
                                    <template v-if="unit.arriendo_total">{{ formatUnitPrice(unit.arriendo_total, unit.moneda) }} <small>/ {{ $t('ficha_nueva.month') }}</small></template>
                                    <template v-else>{{ formatUnitPrice(unit.arriendo_diario, unit.moneda) }} <small>/ {{ $t('ficha_nueva.day') }}</small></template>
                                </span>
                            </span>
                        </button>
                    </div>
                </section>

                <!-- Alojamiento: datos + pestañas Reglas · Características · Espacios -->
                <section v-else class="fx-card fx-tb" :aria-label="$t('ficha_nueva.info')">
                    <ul class="fx-chips-d">
                        <li v-for="c in datos" :key="c.k">
                            <svg v-if="iconosDatos[c.k]" class="fx-chips-ico" viewBox="0 0 24 24" aria-hidden="true" v-html="iconosDatos[c.k]"></svg>
                            <span>{{ c.t }}</span>
                        </li>
                    </ul>

                    <div class="fx-tb-bar" role="tablist" :aria-label="$t('ficha_nueva.info')" @keydown="flechasTabs">
                        <button
                            v-for="t in tabs"
                            :key="t.k"
                            :id="'fx-tab-' + t.k"
                            type="button"
                            role="tab"
                            class="fx-tb-t"
                            :aria-selected="String(tab === t.k)"
                            :aria-controls="'fx-panel'"
                            :tabindex="tab === t.k ? 0 : -1"
                            @click="tab = t.k"
                        >{{ t.label }}</button>
                    </div>

                    <div id="fx-panel" class="fx-tb-panel" role="tabpanel" :aria-labelledby="'fx-tab-' + tab" tabindex="0">

                        <!-- Reglas -->
                        <div v-show="tab === 'reglas'">
                            <FxLeerMas><AdminTextoEditable :accommodation="accommodation" campo="reglas" :vacio="$t('ficha_nueva.no_rules')" /></FxLeerMas>

                            <!-- Costos administrativos (feed de Spacest): texto libre, tal cual -->
                            <div v-if="adminFees.length" class="fx-fees">
                                <p class="fx-fees-t">{{ $t('ficha_nueva.fees_title') }}</p>
                                <p class="fx-fees-d">{{ $t('ficha_nueva.fees_desc') }}</p>
                                <ul>
                                    <li v-for="fee in adminFees" :key="fee.key"><span>{{ $t('ficha_nueva.fee_' + fee.key) }}</span><span>{{ fee.value }}</span></li>
                                </ul>
                            </div>

                            <div class="fx-host">
                                <span class="fx-host-av" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg></span>
                                <div class="fx-host-b">
                                    <p class="fx-host-t">{{ $t('ficha_nueva.about_us') }}</p>
                                    <FxLeerMas><AdminTextoEditable :accommodation="accommodation" campo="sobre_nosotros" /></FxLeerMas>
                                </div>
                            </div>
                        </div>

                        <!-- Características -->
                        <div v-show="tab === 'caracteristicas'">
                            <AdminCaracteristicasEditable :accommodation="accommodation" />

                            <template v-if="tuEspacio.length">
                                <p class="fx-amen-sub fx-amen-sub--first">{{ $t('ficha_nueva.your_space') }}</p>
                                <ul class="fx-amen">
                                    <li v-for="it in tuEspacio" :key="'r' + it.k"><img :src="it.icon" alt="" /><span>{{ it.t }}</span><span v-if="it.n > 1" class="fx-amen-n">{{ it.n }}</span></li>
                                </ul>
                            </template>

                            <p :class="['fx-amen-sub', { 'fx-amen-sub--first': !tuEspacio.length }]">{{ $t('ficha_nueva.amenities') }}</p>
                            <ul v-if="comodidades.length" class="fx-amen">
                                <li v-for="it in comodidades" :key="'h' + it.k"><img :src="it.icon" alt="" /><span>{{ it.t }}</span><span v-if="it.n > 1" class="fx-amen-n">{{ it.n }}</span></li>
                            </ul>
                            <p v-else class="fx-muted">{{ $t('ficha_nueva.no_amenities') }}</p>

                            <template v-if="cercanias.length">
                                <div class="fx-near-h">
                                    <p class="fx-amen-sub">{{ $t('accommodation.close-to') }}</p>
                                    <button type="button" class="fx-pill fx-pill--sm" @click="verMapa">{{ $t('ficha_nueva.see_on_map') }}</button>
                                </div>
                                <div class="fx-near">
                                    <span v-for="it in cercanias" :key="'l' + it.k" class="fx-mini"><img :src="it.icon" alt="" />{{ it.t }}</span>
                                </div>
                            </template>
                        </div>

                        <!-- Espacios: lista a la izquierda, texto completo a la derecha -->
                        <div v-show="tab === 'espacios'" class="fx-esp">
                            <div class="fx-esp-list" role="tablist" aria-orientation="vertical" :aria-label="$t('ficha_nueva.tab_spaces')" @keydown="flechasTabs">
                                <button
                                    v-for="e in espacios"
                                    :key="e.k"
                                    :id="'fx-esp-' + e.k"
                                    type="button"
                                    role="tab"
                                    class="fx-esp-b"
                                    :aria-selected="String(esp === e.k)"
                                    aria-controls="fx-esp-body"
                                    :tabindex="esp === e.k ? 0 : -1"
                                    @click="esp = e.k"
                                >
                                    <span class="fx-esp-ico" aria-hidden="true" v-html="e.svg"></span>
                                    <span class="fx-esp-txt"><strong>{{ e.t }}</strong><span>{{ e.preview }}</span></span>
                                </button>
                            </div>
                            <div id="fx-esp-body" class="fx-esp-body" role="tabpanel" :aria-labelledby="'fx-esp-' + esp">
                                <template v-for="e in espacios">
                                    <div v-show="esp === e.k" :key="'b' + e.k">
                                        <h3 class="fx-esp-t"><span aria-hidden="true" v-html="e.svg"></span>{{ e.t }}</h3>
                                        <FxLeerMas><AdminTextoEditable :accommodation="accommodation" :campo="e.k" /></FxLeerMas>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <!-- ── Reserva ── -->
            <aside id="fx-reserva" class="fx-side" :aria-label="$t('ficha_nueva.booking')">
                <template v-if="isBuilding">
                    <div v-if="selectedUnit" class="fx-unitbox">
                        <span>{{ $t('ficha_nueva.unit_n', { id: selectedUnit.idalojamiento }) }}<template v-if="selectedUnit.idcodigo"> · {{ selectedUnit.idcodigo }}</template></span>
                        <button type="button" :aria-label="$t('ficha_nueva.unit_other')" @click="selectedUnit = null">×</button>
                    </div>
                    <BookingDetails v-if="selectedUnit" :key="selectedUnit.idalojamiento" :image="user.imagen" :accommodation="selectedUnit" />
                    <div v-else class="fx-bk-empty">
                        <p class="fx-bk-empty-t">{{ $t('ficha_nueva.pick_unit_title') }}</p>
                        <p>{{ $tc('ficha_nueva.pick_unit_desc', buildingUnits.length, { n: buildingUnits.length }) }}</p>
                    </div>
                </template>
                <BookingDetails v-else :image="user.imagen" :accommodation="accommodation" />
            </aside>
        </div>

        <!-- ══ HERRAMIENTAS DE ADMIN ════════════════════════════════════ -->
        <AdminBar v-if="isAdmin" :data="accommodation" />
      </div>

      <!-- Celular / tablet: barra fija que lleva a la caja de reserva -->
      <div class="fx-bar">
          <div class="fx-bar-p">
              <strong v-if="precioBarra">{{ precioBarra }} <small>/ {{ precioBarraPer }}</small></strong>
              <span>{{ isBuilding && !selectedUnit ? $t('ficha_nueva.pick_unit_title') : $t('ficha_nueva.pick_dates') }}</span>
          </div>
          <button type="button" class="fx-btn fx-btn--primary" @click="irAReserva">{{ isBuilding && !selectedUnit ? $t('ficha_nueva.see_units') : $t('pay.reserve') }}</button>
      </div>

      <FxGaleria ref="galeria" :fotos="fotos" :titulo="displayTitle" :subtitulo="$tc('ficha_nueva.n_photos', fotos.length, { n: fotos.length })" />
    </div>
    </transition>
</div>
</template>

<script>
import axios from "axios";
import Loader from "@/components/Ui/Loader.vue";
import { mapActions, mapState, mapGetters } from "vuex";
import CarouselList from "@/components/Carousel/CarouselList";
import BookingDetails from "@/components/DetalleAlojamiento/BookingDetails";
import GmapCustomMarker from "vue2-gmap-custom-marker";

import houseData from "@/components/DetalleAlojamiento/data/house-data.json";
import locationData from "@/components/DetalleAlojamiento/data/location-data.json";
import roomData from "@/components/DetalleAlojamiento/data/room-data.json";
import categories from "@/components/Banner/Category.json";
import DetalleAlojamientoLocation from "@/components/DetalleAlojamiento/DetalleAlojamientoLocation";
import FxGaleria from "@/components/DetalleAlojamiento/ficha/FxGaleria";
import FxLeerMas from "@/components/DetalleAlojamiento/ficha/FxLeerMas";
import AdminBar from "../../components/Admin/AdminBar";
import AdminTextoEditable from "@/components/Admin/Ficha/AdminTextoEditable";
import AdminCaracteristicasEditable from "@/components/Admin/Ficha/AdminCaracteristicasEditable";
import { columnaCaracteristica, textoHtml, htmlATexto } from "@/Utils/textoIdiomas";
import { setAccommodationJsonLd, removeAccommodationJsonLd } from "@/Utils/seo";
import NotFound from "@/pages/General/NotFound.vue";

// Íconos de las 3 secciones de "Espacios" (trazo, 24×24)
const SVG = {
    sofa: '<svg viewBox="0 0 24 24"><path d="M4 11V8a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3"/><path d="M2.5 13a2 2 0 0 1 4 0v2h11v-2a2 2 0 0 1 4 0v5h-19v-5ZM5 18v2M19 18v2"/></svg>',
    chef: '<svg viewBox="0 0 24 24"><path d="M6 13.5V20h12v-6.5"/><path d="M6 13.5A4 4 0 0 1 7.5 6a4.5 4.5 0 0 1 9 0A4 4 0 0 1 18 13.5"/><path d="M6 17h12"/></svg>',
    broom: '<svg viewBox="0 0 24 24"><path d="M14 3 10 11"/><path d="M6.5 11h7.5l1.5 10H5l1.5-10Z"/><path d="M9 15v6M12 15v6"/></svg>',
};

export default {

	name: "DetalleAlojamiento",
	components: {
		NotFound,
		BookingDetails,
		DetalleAlojamientoLocation,
		FxGaleria,
		FxLeerMas,
		AdminBar,
		AdminTextoEditable,
		AdminCaracteristicasEditable,
		"gmap-custom-marker": GmapCustomMarker,
		CarouselList,
            Loader,
	},
	data() {
		return {
            accommodationFetched: false,
            noEncontrado: false,
            activeTab: 'descripcion',
            galleryMode: 'images',
            galleryExpanded: false,
            activeRoom: null,
            openAccordion: null,
            selectedUnit: null,
            headerH: 56,   // alto del header, para la pantalla de carga
            houseData,
            locationData,
            // Diseño nuevo de la ficha
            vista: 'fotos',          // fotos | mapa (en el mismo lugar, como la barra Imágenes / Mapa)
            tab: 'reglas',           // reglas | caracteristicas | espacios
            esp: 'espacios_comunes', // sección elegida dentro de Espacios
            fotoMovil: 0,
		};
	},
	methods: {
        ...mapActions('accommodation', [
            "fetchAccommodationById",
            "setAccommodation"
        ]),
        
        // Alto real del header sticky (varía con el ancho de pantalla).
        medirHeader() {
            const h = document.querySelector('.app-header');
            if (h) this.headerH = Math.round(h.getBoundingClientRect().height);
        },

        async markShortListAsOpened(shortListId) {
            if (!shortListId) {
                return;
            }

            try {
                await axios.post(`${process.env.VUE_APP_API_URL}dashboard/short-list/${encodeURIComponent(shortListId)}/open`);
            } catch (e) {
                // No interrumpimos la experiencia del usuario si falla el tracking
                console.error('[HyF][ShortList] Error al marcar como abierta:', shortListId, e);
            }
        },

        photoUrl(url) {
            const s3BaseUrl = process.env.VUE_APP_S3_URL || '';
            const finalUrl = this.normalizeS3ImageUrl(url, s3BaseUrl);

            if (!url) return '';
            return finalUrl;
        },

        normalizeS3ImageUrl(url, s3BaseUrl) {
            if (!url) return '';

            const bucket = (process.env.VUE_APP_AWS_BUCKET_URL || '').replace(/^\/+|\/+$/g, '');
            if (url.indexOf('http') === -1) {
                return s3BaseUrl + url;
            }

            if (!bucket || url.indexOf('s3-sa-east-1.amazonaws.com') === -1 || url.indexOf(bucket) > -1) {
                return url;
            }

            try {
                const parsedUrl = new URL(url);
                parsedUrl.pathname = `/${bucket}/${parsedUrl.pathname.replace(/^\/+/, '')}`;
                return parsedUrl.toString();
            } catch (e) {
                return url;
            }
        },

        formatUnitPrice(amount, currency) {
            const parsedAmount = Number(amount);

            if (!Number.isFinite(parsedAmount)) {
                return `${currency || ''} ${amount}`.trim();
            }

            return `${currency || ''} ${parsedAmount.toLocaleString('es-CL')}`.trim();
        },

        async loadAccommodation(accommodationId) {
            this.noEncontrado = false;
            try {
                await this.fetchAccommodationById(accommodationId);
            } catch (e) {
                this.setAccommodation(null);
            }
            // La API responde `accomodation: null` si no existe, se eliminó o
            // está sin aprobar y no es tuyo.
            if (!this.accommodation) {
                this.noEncontrado = true;
                return;
            }
            this.syncSelectedUnitFromRoute();
            this.updateJsonLd();
        },

        // Arma el payload del JSON-LD a partir del alojamiento ya cargado y lo
        // inyecta en el <head>. Reutiliza la misma info que muestra la ficha
        // (título, fotos, precio) para que el schema no contradiga a la vista.
        // Si no hay alojamiento todavía, no hace nada: se vuelve a llamar cuando
        // termina de cargar.
        updateJsonLd() {
            const a = this.accommodation;
            if (!a) return;

            // Precio mensual preferido; si el alojamiento es por día, el diario.
            const esDiario = a.modalidad_tiempo === 'diario';
            const precio = Number(esDiario ? a.arriendo_diario : (a.arriendo_total || a.arriendo)) || 0;

            setAccommodationJsonLd({
                name: this.displayTitle,
                description: this.metaDescriptionText,
                url: window.location.href,
                sku: a.idcodigo || a.idalojamiento,
                images: this.fotos,
                floorSize: a.m2,
                occupancy: a.maximo_huespedes,
                addressText: a.direccion,
                price: precio,
                currency: a.moneda,
            });
        },

        syncSelectedUnitFromRoute() {
            if (!this.accommodation) {
                this.selectedUnit = null;
                return;
            }

            if (this.isBuilding && this.$route.query.unidad === '1') {
                this.selectedUnit = this.accommodation;
                return;
            }

            this.selectedUnit = null;
        },

        selectRoom(room) {
            if (this.activeRoom && this.activeRoom.idpieza === room.idpieza) {
                this.activeRoom = null;
            } else {
                this.activeRoom = room;
                this.galleryMode = 'images';
                this.galleryExpanded = false;
            }
        },

        toggleAccordion(key) {
            this.openAccordion = this.openAccordion === key ? null : key;
        },

        showFeatureField(item) {
            return (item.studio === true && this.accommodation.room.length === 0) || !item.studio;
        },

        // Columna real de una característica de house-data / location-data
        // (algunas tienen otro nombre en esas listas y nunca se mostraban).
        colCaract(item) {
            return columnaCaracteristica(item.field);
        },

        icono(item) {
            try { return require(`@/assets/alojamiento/${item.icon}`); } catch (e) { return null; }
        },

        abrirGaleria(i) {
            if (this.$refs.galeria) this.$refs.galeria.abrir(i);
        },

        alDeslizar(e) {
            const t = e.target;
            this.fotoMovil = Math.round(t.scrollLeft / Math.max(1, t.clientWidth));
        },

        verMapa() {
            this.vista = 'mapa';
            this.$nextTick(() => {
                const el = this.$el.querySelector('.fx-md');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },

        // Celular / tablet: la caja de reserva va debajo del contenido.
        irAReserva() {
            const id = this.isBuilding && !this.selectedUnit ? '.fx-units' : '#fx-reserva';
            const el = this.$el.querySelector(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        // Pestañas con flechas (patrón ARIA tabs)
        flechasTabs(e) {
            const lista = [...e.currentTarget.querySelectorAll('[role="tab"]')];
            const i = lista.indexOf(document.activeElement);
            if (i < 0) return;
            const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
            let n = null;
            if (d) n = (i + d + lista.length) % lista.length;
            if (e.key === 'Home') n = 0;
            if (e.key === 'End') n = lista.length - 1;
            if (n === null) return;
            e.preventDefault();
            lista[n].click();
            this.$nextTick(() => lista[n].focus());
        },

        async copiar(texto) {
            try { await navigator.clipboard.writeText(String(texto)); } catch (e) {
                const ta = document.createElement('textarea');
                ta.value = String(texto); ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select();
                try { document.execCommand('copy'); } catch (e2) { /* sin permiso */ }
                ta.remove();
            }
            this.$toasted.show(this.$t('ficha_nueva.copied', { v: texto }), { theme: 'toasted-primary', position: 'bottom-right', duration: 2200, className: 'toasted' });
        },

        async compartir() {
            const url = window.location.href;
            if (navigator.share) {
                try { await navigator.share({ title: this.displayTitle, url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
            }
            this.copiar(url);
        },

        selectUnit(unit) {
            const unitId = String(unit.idalojamiento);
            const currentId = String(this.accommodation.idalojamiento);

            if (this.selectedUnit && String(this.selectedUnit.idalojamiento) === unitId) {
                // Clic en la misma unidad: des-seleccionar
                this.selectedUnit = null;
                if (this.$route.query.unidad === '1') {
                    this.$router.replace({
                        path: `/alojamiento/${currentId}`,
                        query: {
                            ...this.$route.query,
                            unidad: undefined,
                        },
                    }).catch(() => {});
                }
            } else {
                // Si la unidad seleccionada es la misma que la URL actual, usamos
                // el objeto accommodation completo (tiene todas las relaciones cargadas)
                if (unitId === currentId) {
                    this.selectedUnit = this.accommodation;
                } else {
                    // Para otras unidades navegamos a su URL (carga el objeto completo)
                    this.$router.push({
                        path: `/alojamiento/${unitId}`,
                        query: {
                            ...this.$route.query,
                            unidad: '1',
                        },
                    }).catch(() => {});
                }
            }
        },
    },
    computed: {
        ...mapState('accommodation', [
            "accommodation",
        ]),
        ...mapState(["user"]),
        ...mapGetters(["isAdmin"]),


        title() {
            // The title is built with the categoría + tipo - alojamientoid
            return this.$t('accommodation.title.' + this.accommodation.tipo + '-' + this.accommodation.categoria)
        },

        // Descripción corta y segura para el JSON-LD: se arma con datos
        // estructurados (m², huéspedes, mínimo de estadía) en vez del texto
        // libre de la ficha, que es HTML por idioma y puede venir vacío. Si no
        // hay ningún dato, cae a un texto genérico.
        metaDescriptionText() {
            const a = this.accommodation;
            if (!a) return '';

            const partes = [];
            if (Number(a.m2) > 0) partes.push(`${a.m2} m²`);
            if (Number(a.maximo_huespedes) > 0) {
                partes.push(this.$tc('ficha_nueva.guests', Number(a.maximo_huespedes), { n: a.maximo_huespedes }));
            }
            const min = Number(a.tiempo_min);
            if (min > 0) {
                partes.push(min >= 30 && min % 30 === 0
                    ? this.$tc('ficha_nueva.min_months', min / 30, { n: min / 30 })
                    : this.$tc('ficha_nueva.min_nights', min, { n: min }));
            }

            const base = this.displayTitle || 'Alojamientos temporales';
            return partes.length ? `${base} · ${partes.join(' · ')}` : String(base);
        },

        isBuilding() {
            return !!(this.accommodation &&
                this.accommodation.propiedad_idpropiedad &&
                this.accommodation.propiedad);
        },

        buildingUnits() {
            if (!this.isBuilding) return [];
            return this.accommodation.propiedad.units || [];
        },

        displayTitle() {
            if (this.isBuilding) {
                return this.accommodation.propiedad.nombre || this.title;
            }
            return this.title;
        },

        hasRooms() {
            return !this.isBuilding &&
                this.accommodation &&
                Array.isArray(this.accommodation.room) &&
                this.accommodation.room.length > 0;
        },

        galleryPhotos() {
            if (this.activeRoom && this.activeRoom.gallery && this.activeRoom.gallery.length) {
                return this.activeRoom.gallery;
            }
            // Para edificios usar la galería del edificio
            if (this.isBuilding && this.accommodation.propiedad.gallery && this.accommodation.propiedad.gallery.length) {
                return this.accommodation.propiedad.gallery;
            }
            return this.accommodation ? (this.accommodation.photos || []) : [];
        },

        /**
         * Costos administrativos del feed de Spacest (`admin_fee_open` y
         * `admin_fee_close`): gastos de una sola vez, aparte del arriendo y de
         * los servicios.
         *
         * Son texto libre escrito listing por listing ("250 EUR agency fee"),
         * no números: el monto, la moneda y la redacción no siguen un patrón
         * fijo. Por eso van tal cual a la vista, sin parsear y sin formatear.
         *
         * Los null quedan fuera del array, así no se renderiza ni "null" ni 0
         * ni una fila vacía.
         */
        adminFees() {
            const spacest = this.accommodation && this.accommodation.spacest;

            if (!spacest) {
                return [];
            }

            return [
                { key: 'open',  label: 'Al iniciar el contrato',   value: spacest.admin_fee_open },
                { key: 'close', label: 'Al finalizar el contrato', value: spacest.admin_fee_close },
            ].filter(fee => typeof fee.value === 'string' && fee.value.trim() !== '');
        },

        // Fotos con la URL ya resuelta. Si el alojamiento no tiene fotos propias,
        // se usan las de la habitación (cada alojamiento es una habitación).
        fotos() {
            let lista = this.galleryPhotos;
            if ((!lista || !lista.length) && this.accommodation && Array.isArray(this.accommodation.room)) {
                lista = [].concat(...this.accommodation.room.map(r => r.gallery || []));
            }
            return (lista || []).map(f => this.photoUrl(f && f.url)).filter(Boolean);
        },
        mosaico() {
            const n = this.fotos.length;
            return this.fotos.slice(0, n >= 5 ? 5 : n >= 3 ? 3 : 1);
        },

        tabs() {
            return [
                { k: 'reglas', label: this.$t('ficha_nueva.tab_rules') },
                { k: 'caracteristicas', label: this.$t('ficha_nueva.tab_features') },
                { k: 'espacios', label: this.$t('ficha_nueva.tab_spaces') },
            ];
        },

        // Íconos de las píldoras (mismos trazos que la demo: 24×24, trazo 1.8)
        iconosDatos() {
            return {
                tipo: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h5v-6h4v6h5V9.5"/>',
                cat: '<rect x="4" y="3" width="11" height="18" rx="1.5"/><path d="M15 9h3.5A1.5 1.5 0 0 1 20 10.5V21M8 7h3M8 11h3M8 15h3M3 21h18"/>',
                m2: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
                hu: '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14a6.5 6.5 0 0 1 3.5 6"/>',
                min: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
                mod: '<rect x="3" y="4.5" width="18" height="16.5" rx="2.5"/><path d="M16 2.5v4M8 2.5v4M3 10h18"/>',
            };
        },

        // Datos principales en píldoras
        datos() {
            const a = this.accommodation;
            const out = [];
            if (a.tipo && this.$te('search.types.' + a.tipo)) out.push({ k: 'tipo', t: this.$t('search.types.' + a.tipo) });
            const cat = categories.find(c => c.value === a.categoria);
            if (cat) out.push({ k: 'cat', t: this.$t(cat.translated) });
            if (Number(a.m2) > 0) out.push({ k: 'm2', t: `${a.m2} m²` });
            if (Number(a.maximo_huespedes) > 0) out.push({ k: 'hu', t: this.$tc('ficha_nueva.guests_up_to', Number(a.maximo_huespedes), { n: a.maximo_huespedes }) });
            const min = Number(a.tiempo_min);
            if (min > 0) out.push({ k: 'min', t: min >= 30 && min % 30 === 0 ? this.$tc('ficha_nueva.min_months', min / 30, { n: min / 30 }) : this.$tc('ficha_nueva.min_nights', min, { n: min }) });
            if (['mensual', 'diario', 'ambos'].includes(a.modalidad_tiempo)) out.push({ k: 'mod', t: this.$t('ficha_nueva.modality.' + a.modalidad_tiempo) });
            return out;
        },

        // "Tu espacio": equipamiento de la habitación o, si no tiene, del monoambiente
        tuEspacio() {
            const a = this.accommodation;
            const room = Array.isArray(a.room) && a.room.length ? a.room[0] : null;
            const lista = room
                ? roomData.map(it => ({ it, n: Number(room[it.field]) || 0 }))
                : houseData.filter(it => it.studio === true).map(it => ({ it, n: Number(a[this.colCaract(it)]) || 0 }));
            return lista.filter(x => x.n > 0).map(x => ({ k: x.it.field, t: this.$t(x.it.text), icon: this.icono(x.it), n: x.n }));
        },
        comodidades() {
            const a = this.accommodation;
            return houseData
                .filter(it => !it.studio && Number(a[this.colCaract(it)]) > 0)
                .map(it => ({ k: it.field, t: this.$t(it.text), icon: this.icono(it), n: Number(a[this.colCaract(it)]) }));
        },
        cercanias() {
            const a = this.accommodation;
            return locationData
                .filter(it => Number(a[this.colCaract(it)]) > 0)
                .map(it => ({ k: it.field, t: this.$t(it.text), icon: this.icono(it) }));
        },

        espacios() {
            const a = this.accommodation;
            const camper = a.tipo === 'camper';
            const prev = campo => {
                const txt = htmlATexto(textoHtml(a[campo], this.$i18n.locale));
                return txt ? (txt.length > 80 ? txt.slice(0, 80).trim() + '…' : txt) : this.$t('ficha_nueva.no_info');
            };
            return [
                { k: 'espacios_comunes', t: camper ? this.$t('accommodation.common-spaces-camper') : this.$t('accommodation.common-spaces'), svg: SVG.sofa, preview: prev('espacios_comunes') },
                { k: 'uso_cocina', t: camper ? this.$t('accommodation.kitchen-camper') : this.$t('accommodation.kitchen-use'), svg: SVG.chef, preview: prev('uso_cocina') },
                { k: 'aseo_limpieza', t: this.$t('accommodation.cleaning'), svg: SVG.broom, preview: prev('aseo_limpieza') },
            ];
        },

        // Precio de la barra fija (celular), con el mismo filtro que Price.vue
        precioBarra() {
            const a = this.isBuilding ? this.selectedUnit : this.accommodation;
            if (!a) return '';
            const mensual = a.modalidad_tiempo !== 'diario';
            const monto = mensual ? a.arriendo : a.arriendo_diario;
            if (!Number(monto)) return '';
            const f = this.$options.filters && this.$options.filters.convertPriceToInteger;
            return f ? f({ amount: monto, currency: a.moneda }) : `${a.moneda} ${monto}`;
        },
        precioBarraPer() {
            const a = this.isBuilding ? this.selectedUnit : this.accommodation;
            return a && a.modalidad_tiempo === 'diario' ? this.$t('ficha_nueva.day') : this.$t('ficha_nueva.month');
        },

        visibleTabs() {
            const tabs = [
                { key: 'descripcion', label: 'Reglas' },
                { key: 'habitaciones', label: 'Habitaciones', show: this.hasRooms },
                { key: 'anfitrion', label: 'Características' },
                { key: 'comodidades', label: 'Espacios' },
            ];
            return tabs.filter(t => t.show !== false);
        },

    },
    watch: {
        '$route.params.idalojamiento': {
            async handler(newId) {
                if (newId) {
                    this.vista = 'fotos'; this.fotoMovil = 0;
                    await this.loadAccommodation(newId);
                }
            },
        },
        '$route.query.unidad': {
            handler() {
                this.syncSelectedUnitFromRoute();
            },
        },
    },
    
    async created() {

        window.scrollTo(0, 0);

        // Fetch accommodation
        const accommodationId = this.$router.currentRoute.params.idalojamiento;
        await this.loadAccommodation(accommodationId);

        // El conteo de visitas se quitó a pedido (la columna `visitas` sigue en
        // la base, solo dejó de sumarse y de mostrarse).

        // Si el enlace viene con query ?estado={short_list_id}, marcamos ese registro como "abierta"
        const shortListId = this.$route.query.estado;
        if (shortListId) {
            this.markShortListAsOpened(shortListId);
        }
        
    },

    mounted() {
        document.documentElement.classList.add('fx-page');
        // El header cambia de alto mientras carga (fuente, logo) y con el ancho:
        // se sigue con ResizeObserver; resize como respaldo.
        this.medirHeader();
        const h = document.querySelector('.app-header');
        if (h && typeof ResizeObserver !== 'undefined') {
            this.headerObserver = new ResizeObserver(() => this.medirHeader());
            this.headerObserver.observe(h);
        } else {
            window.addEventListener('resize', this.medirHeader);
        }
        // El aviso de cookies (fijo abajo) tapa la barra de reserva del celular:
        // la barra se sube su alto mientras el aviso esté visible.
        const ck = document.querySelector('.cookie-footer');
        if (ck && typeof ResizeObserver !== 'undefined') {
            this.cookieObserver = new ResizeObserver(() => {
                const alto = ck.isConnected ? ck.offsetHeight : 0;
                document.documentElement.style.setProperty('--fx-cookie', alto + 'px');
                if (!alto) this.cookieObserver.disconnect();
            });
            this.cookieObserver.observe(ck);
        }
    },

    beforeDestroy() {
        removeAccommodationJsonLd();
        document.documentElement.classList.remove('fx-page', 'fx-lock');
        document.documentElement.style.removeProperty('--fx-cookie');
        if (this.cookieObserver) this.cookieObserver.disconnect();
        if (this.headerObserver) this.headerObserver.disconnect();
        window.removeEventListener('resize', this.medirHeader);
    },
};
</script>

<style scoped lang="scss">

// ── Wrapper ──────────────────────────────────────────────────────────────────
// Pantalla de carga: `.main-content` (DefaultLayout) suma un padding-top de
// $desktop/$mobile-header-height, que se sube. El alto es el visor menos el
// header sticky, que se mide en JS (`--da-header`: cambia según el ancho).
.da-loading {
    position: relative;
    margin-top: -$mobile-header-height;
    height: calc(100vh - var(--da-header, 56px));
    height: calc(100dvh - var(--da-header, 56px));

    @media (min-width: $md) {
        margin-top: -$desktop-header-height;
    }
}

// Salida suave del loader: mientras se desvanece queda superpuesto (absolute)
// en el mismo lugar, así el contenido no se corre hacia abajo.
.da-loader-out-leave-active {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 60;
    transition: opacity .55s ease, transform .55s ease;
}
.da-loader-out-leave-to {
    opacity: 0;
    transform: scale(1.015);
}

// El contenido entra con un leve fundido y desplazamiento.
.da-content-in-enter-active {
    transition: opacity .55s ease .1s, transform .55s ease .1s;
}
.da-content-in-enter {
    opacity: 0;
    transform: translateY(10px);
}

@media (prefers-reduced-motion: reduce) {
    .da-loader-out-leave-active,
    .da-content-in-enter-active { transition: opacity .2s linear; }
    .da-loader-out-leave-to,
    .da-content-in-enter { transform: none; }
}

.da-wrapper { position: relative; }
</style>

<!-- Diseño nuevo de la ficha (global: los modales de Bootstrap van al body) -->
<style src="../../components/DetalleAlojamiento/ficha/ficha.css"></style>
